/**
 * User Helper Utility
 * 
 * Helper functions to get user data from localStorage
 */

import { getAuthUser, getUserProfile } from './tokenManager';
import { getCachedProfileImage, getCachedBusinessImage } from './imageCache';

// Default business name fallback
const DEFAULT_BUSINESS_NAME = 'Starlight Cafe';

/**
 * Format plan name to display format
 * Converts 'starter' -> 'Starter Plan', 'business' -> 'Business Plan', etc.
 */
const formatPlanName = (plan: string): string => {
  if (!plan) return 'Starter Plan';
  
  // Convert to title case
  const formatted = plan
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  // Add "Plan" suffix if not already present
  return formatted.includes('Plan') ? formatted : `${formatted} Plan`;
};

/**
 * Get the business name from stored profile data
 */
export const getUserBusinessName = (): string => {
  try {
    // First try to get from profile
    const profile = getUserProfile();
    if (profile?.business_name) {
      return profile.business_name;
    }
    
    // Fallback to user data (for backward compatibility)
    const user = getAuthUser();
    return user?.business_name || DEFAULT_BUSINESS_NAME;
  } catch (error) {
    console.error('Error getting business name:', error);
    return DEFAULT_BUSINESS_NAME;
  }
};

/**
 * Get full user data formatted for UI components
 */
export const getFullUserData = () => {
  try {
    const user = getAuthUser();
    const profile = getUserProfile();
    
    if (!user) {
      return {
        name: 'User',
        businessName: DEFAULT_BUSINESS_NAME,
        avatar: 'https://i.pravatar.cc/150?img=12',
        businessLogo: null,
        usagePlan: 'Starter Plan'
      };
    }

    // Use profile data if available, fallback to user data
    const firstName = profile?.first_name || user.first_name || '';
    const lastName = profile?.second_name || user.last_name || '';
    const businessName = profile?.business_name || user.business_name || DEFAULT_BUSINESS_NAME;
    
    // Try to get from cache first, then from profile data
    const cachedBusinessImage = getCachedBusinessImage();
    const cachedProfileImage = getCachedProfileImage();
    
    // Get business logo from cache or profile (supports multiple field names from API)
    const businessLogo = cachedBusinessImage ||
                        profile?.business_image_url || 
                        profile?.business_logo_url || 
                        null;

    // Get profile image from cache or profile API, fallback to default avatar
    const profileImage = cachedProfileImage ||
                         profile?.profile_image_url || 
                         'https://i.pravatar.cc/150?img=12';

    // Get usage plan from profile, default to "Starter Plan"
    const usagePlan = profile?.usage_plan 
      ? formatPlanName(profile.usage_plan) 
      : 'Starter Plan';

    return {
      name: `${firstName} ${lastName}`.trim() || 'User',
      businessName: businessName,
      avatar: profileImage,
      businessLogo: businessLogo,
      usagePlan: usagePlan
    };
  } catch (error) {
    console.error('Error getting user data:', error);
    return {
      name: 'User',
      businessName: DEFAULT_BUSINESS_NAME,
      avatar: 'https://i.pravatar.cc/150?img=12',
      businessLogo: null,
      usagePlan: 'Starter Plan'
    };
  }
};

/**
 * Get user's full name
 */
export const getUserFullName = (): string => {
  try {
    const user = getAuthUser();
    if (!user) return 'User';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';
  } catch (error) {
    console.error('Error getting user full name:', error);
    return 'User';
  }
};

